import React, { useState, useEffect } from "react"
import { supabase } from "../supabaseClient.js"

const Admin = () => {
  const [session, setSession] = useState(null)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [clues, setClues] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [editingClue, setEditingClue] = useState(null)
  const [videoUrl, setVideoUrl] = useState("")
  const [videoTitle, setVideoTitle] = useState("")

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (session) {
      fetchClues()
    }
  }, [session])

  const fetchClues = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from("clues")
      .select("*")
      .order("id", { ascending: true })

    if (error) {
      setMessage("Error fetching clues: " + error.message)
    } else {
      setClues(data || [])
    }
    setLoading(false)
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage("")

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      setMessage("Login failed: " + error.message)
    }
    setLoading(false)
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setSession(null)
  }

  const handleEdit = (clue) => {
    setEditingClue(clue)
    setVideoUrl(clue.video_url || "")
    setVideoTitle(clue.video_title || "")
  }

  const handleSave = async () => {
    if (!editingClue) return

    setLoading(true)
    const { error } = await supabase
      .from("clues")
      .update({
        video_url: videoUrl || null,
        video_title: videoTitle || null,
      })
      .eq("id", editingClue.id)

    if (error) {
      setMessage("Error saving: " + error.message)
    } else {
      setMessage("Saved successfully!")
      setEditingClue(null)
      fetchClues()
    }
    setLoading(false)
  }

  const handleCancel = () => {
    setEditingClue(null)
    setVideoUrl("")
    setVideoTitle("")
  }

  // Login form
  if (!session) {
    return (
      <div className="adminContainer">
        <div className="adminLoginBox">
          <h1 className="adminTitle">Admin Login</h1>
          <form onSubmit={handleLogin} className="adminForm">
            <div className="adminFormGroup">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="adminInput"
              />
            </div>
            <div className="adminFormGroup">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="adminInput"
              />
            </div>
            <button type="submit" className="adminButton" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
          {message && <p className="adminMessage">{message}</p>}
          <a href="/" className="adminBackLink">
            Back to Game
          </a>
        </div>
      </div>
    )
  }

  // Admin dashboard
  return (
    <div className="adminContainer">
      <div className="adminHeader">
        <h1 className="adminTitle">Certforce Admin</h1>
        <button onClick={handleLogout} className="adminLogoutButton">
          Logout
        </button>
      </div>

      {message && <p className="adminMessage">{message}</p>}

      {editingClue ? (
        <div className="adminEditModal">
          <h2>Edit Video for Clue #{editingClue.id}</h2>
          <p className="adminClueQuestion">
            {editingClue.question.substring(0, 100)}...
          </p>
          <div className="adminFormGroup">
            <label htmlFor="videoUrl">
              Video URL (YouTube or Vimeo link)
            </label>
            <input
              id="videoUrl"
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="adminInput"
            />
          </div>
          <div className="adminFormGroup">
            <label htmlFor="videoTitle">
              Video Title (for accessibility)
            </label>
            <input
              id="videoTitle"
              type="text"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              placeholder="Solution walkthrough for..."
              className="adminInput"
            />
          </div>
          <div className="adminEditActions">
            <button
              onClick={handleSave}
              className="adminButton"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button onClick={handleCancel} className="adminCancelButton">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="adminTableContainer">
          {loading ? (
            <p>Loading clues...</p>
          ) : (
            <table className="adminTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Points</th>
                  <th>Question (Preview)</th>
                  <th>Video URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clues.map((clue) => (
                  <tr key={clue.id}>
                    <td>{clue.id}</td>
                    <td>${clue.points}</td>
                    <td className="adminQuestionCell">
                      {clue.question.substring(0, 50)}...
                    </td>
                    <td>
                      {clue.video_url ? (
                        <span className="adminHasVideo">Has video</span>
                      ) : (
                        <span className="adminNoVideo">No video</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => handleEdit(clue)}
                        className="adminEditButton"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <a href="/" className="adminBackLink">
        Back to Game
      </a>
    </div>
  )
}

export default Admin
